// frontend/services/BankAccountService.js

import axiosInstance from './axiosInstance';
import { roundMoney } from '../utils/formatters';

// Simple in-memory cache for bank accounts to avoid repeated requests within a short time window
let bankAccountsCache = null;
let bankAccountsCacheAt = 0;
let bankAccountsInFlight = null;
const BANK_ACCOUNTS_TTL_MS = 60_000; // 60 seconds

export function clearBankAccountsCache() {
  bankAccountsCache = null;
  bankAccountsCacheAt = 0;
}

export function addBankAccount(newBankAccount, onSuccess) {
  // Validate input bank account data
  if (!newBankAccount || typeof newBankAccount !== 'object') {
    throw new Error('Invalid bank account data');
  }
  if (typeof newBankAccount.nickname !== 'string' || !newBankAccount.nickname.trim()) {
    throw new Error('Invalid bank account name');
  }
  if (typeof newBankAccount.required_amount !== 'number' || isNaN(parseFloat(newBankAccount.required_amount))) {
    throw new Error('Invalid required amount');
  }

  return axiosInstance
    .post(`bank-accounts/`, newBankAccount)
    .then((response) => {
      // Validate response
      if (!response.data || !response.data.id || typeof response.data.nickname !== 'string') {
        throw new Error('Invalid response data structure');
      }
      if (typeof onSuccess === 'function') {
        onSuccess(response.data);
      }
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
}

export function getBankAccounts() {
  // Return in-flight promise if a request is already underway
  if (bankAccountsInFlight) {
    return bankAccountsInFlight;
  }

  // Serve from cache if fresh
  if (bankAccountsCache && (Date.now() - bankAccountsCacheAt) < BANK_ACCOUNTS_TTL_MS) {
    return Promise.resolve(bankAccountsCache);
  }

  // Otherwise, fetch and populate cache; also de-dupe concurrent callers
  bankAccountsInFlight = axiosInstance
    .get(`bank-accounts/`)
    .then((response) => {
      const today = new Date();
      // Filter out inactive accounts (where active_end is before today)
      const activeAccounts = response.data.filter(account => {
        if (!account.active_end) return true; // If no end date, account is active
        const endDate = new Date(account.active_end);
        return endDate >= today;
      });
      
      // Sort by display_order, then nickname
      activeAccounts.sort((a, b) => {
        if (a.display_order !== b.display_order) {
          return a.display_order - b.display_order;
        }
        return a.nickname.localeCompare(b.nickname);
      });

      bankAccountsCache = activeAccounts;
      bankAccountsCacheAt = Date.now();
      bankAccountsInFlight = null; // clear guard
      return activeAccounts;
    })
    .catch((error) => {
      bankAccountsInFlight = null; // clear guard on error so future calls can retry
      throw error;
    });

  return bankAccountsInFlight;
}

export async function getBankAccountsForFortnight() {
  try {
    const response = await axiosInstance.get('/get-bank-accounts-in-fortnight/');
    return response.data;
  } catch (error) {
    throw error;
  }
}

export function createTimeLimitedReqAmount(accountId, reqAmount, startDate, endDate, reason = '') {
  // Validate input parameters
  if (!accountId || typeof accountId !== 'number') {
    throw new Error('Invalid account ID');
  }
  
  // Convert to number for validation if needed
  const reqAmountNum = typeof reqAmount === 'string' ? parseFloat(reqAmount) : reqAmount;
  
  if (isNaN(reqAmountNum)) {
    throw new Error('Invalid required amount');
  }
  
  // Create a payload to directly create a required amount record
  const payload = {
    bank_account: accountId,
    amount: reqAmountNum,
    effective_from: startDate,
    effective_until: endDate,
    is_indefinite: endDate === null,
    reason: reason || ''
  };
  
  return axiosInstance
    .post('/bank-account-required-amounts/', payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
}

export async function updateBankAccountReqAmount(accountId, reqAmount, startDate, endDate = null, reason = '') {
  // Validate input parameters
  if (!accountId || typeof accountId !== 'number') {
    throw new Error('Invalid account ID');
  }
  
  const reqAmountNum = parseFloat(roundMoney(reqAmount));
  
  if (isNaN(reqAmountNum)) {
    throw new Error('Invalid required amount');
  }

  // Create a simple, focused payload with the exact fields the backend expects
  const payload = {
    amount: roundMoney(reqAmountNum),
    effective_from: startDate,
    effective_until: endDate,
    is_indefinite: endDate === null,
    reason: reason || ''
  };

  try {
    const response = await axiosInstance.put(`/bank-accounts/${encodeURIComponent(accountId)}/update_required_amount/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export function updateBankAccount(accountId, updatedAccount) {
  // Validate input parameters
  if (!accountId || typeof accountId !== 'number') {
    throw new Error('Invalid account ID');
  }
  if (!updatedAccount || typeof updatedAccount !== 'object') {
    throw new Error('Invalid updated account data');
  }

  return axiosInstance
    .put(`/bank-accounts/${encodeURIComponent(accountId)}/`, updatedAccount)
    .then((response) => {
      if (!response.data || !response.data.id || typeof response.data.name !== 'string') {
        throw new Error('Invalid response data structure');
      }
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
}

export function deleteBankAccount(accountId) {
  // Validate account ID
  if (!accountId || typeof accountId !== 'number') {
    throw new Error('Invalid account ID');
  }

  return axiosInstance
    .delete(`/bank-accounts/${encodeURIComponent(accountId)}/`)
    .then(() => true)
    .catch((error) => {
      throw error;
    });
}

export async function getFortnightBankAccounts() {
  try {
    const response = await axiosInstance.get('/bank-accounts/');
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function updateBankAccountAdjustment(bankAccountId, adjustmentAmount, adjustmentReason, effectiveFrom = null) {
  try {
    const response = await axiosInstance.put(`/bank-accounts/${bankAccountId}/adjustment/`, {
      adjustment_amount: adjustmentAmount,
      adjustment_reason: adjustmentReason,
      effective_from: effectiveFrom,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getRequirementsTotalForBankAccount(bankAccountId, relativeFortnightIndex = 0) {
  try {
    const response = await axiosInstance.get(
      `/bank-accounts/${bankAccountId}/requirements_total/?relative_fortnight_index=${relativeFortnightIndex}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function findDuplicateBankAccounts() {
  try {
    const response = await axiosInstance.get(`/bank-accounts/find_duplicates/`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function getBankAccountRequiredAmountHistory(bankAccountId) {
  try {
    const response = await axiosInstance.get(`/bank-accounts/${bankAccountId}/required_amount_history/`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteBankAccountRequiredAmount(requiredAmountId) {
  try {
    const response = await axiosInstance.delete(`/bank-account-required-amounts/${requiredAmountId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
}
export async function updateBankAccountAmountFromRequirements(bankAccountId, startDate, reason = '') {
  try {
    // Validate input
    if (!bankAccountId || typeof bankAccountId !== 'number') {
      throw new Error('Invalid bank account ID');
    }
    
    if (!startDate) {
      throw new Error('Start date is required');
    }
    
    // Call the backend endpoint to update requirements for bank account
    const payload = {
      start_date: startDate,
      reason: reason || 'Updated from requirements page',
      recalculate: true
    };
    
    try {
      const response = await axiosInstance.post(
        `/bank-accounts/${bankAccountId}/update_from_requirements/`, 
        payload
      );
      
      return response.data;
    } catch (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
}

// Add new function to update bank account display order
export function updateBankAccountDisplayOrder(accountOrders) {
  // Validate input
  if (!Array.isArray(accountOrders)) {
    throw new Error('Account orders must be an array');
  }
  
  // Validate each order object
  for (const order of accountOrders) {
    if (!order.id || typeof order.id !== 'number') {
      throw new Error('Each order must have a valid id');
    }
    if (typeof order.display_order !== 'number') {
      throw new Error('Each order must have a valid display_order');
    }
  }
  
  return axiosInstance
    .post('/bank-accounts/update_display_order/', accountOrders)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
}

// ========================================
// BALANCE-RELATED FUNCTIONS
// Migrated from BalanceService.js to work with BankAccount.balance field
// ========================================

/**
 * Get balance for a specific bank account
 * @param {number} bankAccountId - The bank account ID
 * @returns {Object} - Balance data in format compatible with old BalanceService
 */
export async function getBalance(bankAccountId) {
  try {
    if (!bankAccountId || typeof bankAccountId !== 'number') {
      throw new Error('Invalid bank account ID');
    }

    const response = await axiosInstance.get(`/bank-accounts/${encodeURIComponent(bankAccountId)}/`);
    
    // Validate response
    if (!response.data || typeof response.data.id !== 'number') {
      throw new Error('Invalid bank account response');
    }

    const bankAccount = response.data;
    
    // Return balance data in format compatible with old BalanceService
    return {
      id: bankAccount.id, // Use bank account ID as balance ID for compatibility
      amount: bankAccount.balance ? bankAccount.balance.toString() : "0.00",
      bank_account_id: bankAccount.id,
      bank_account_nickname: bankAccount.nickname,
      update_date: new Date().toISOString().split('T')[0] // Current date
    };
  } catch (error) {
    // Return default balance object on error for compatibility
    return { 
      id: bankAccountId, 
      amount: "0.00", 
      bank_account_id: bankAccountId,
      bank_account_nickname: null,
      update_date: new Date().toISOString().split('T')[0]
    };
  }
}

/**
 * Get all balances for all bank accounts
 * @returns {Object} - All balances grouped by bank account ID (compatible with old getAllBalances)
 */
export async function getAllBalances() {
  try {
    const response = await axiosInstance.get('/bank-accounts/');
    
    // Validate response
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid bank accounts response');
    }

    // Transform bank accounts data to match old getAllBalances format
    const balancesByAccount = {};
    
    response.data.forEach(bankAccount => {
      balancesByAccount[bankAccount.id.toString()] = {
        id: bankAccount.id,
        amount: bankAccount.balance ? bankAccount.balance.toString() : "0.00",
        bank_account_id: bankAccount.id,
        bank_account_nickname: bankAccount.nickname,
        update_date: new Date().toISOString().split('T')[0]
      };
    });
    
    return balancesByAccount;
  } catch (error) {
    throw error;
  }
}

/**
 * Update balance for a bank account
 * @param {number} bankAccountId - The bank account ID (used as balanceId for compatibility)
 * @param {Object} data - Balance update data
 * @returns {Object} - Updated balance data
 */
export async function updateBalance(bankAccountId, data) {
  try {
    // Validate input data
    if (!data || isNaN(parseFloat(data.amount))) {
      throw new Error('Invalid balance amount input');
    }

    // Validate bankAccountId (used as balanceId in old system)
    if (!bankAccountId || typeof bankAccountId !== 'number') {
      throw new Error('Invalid bank account ID');
    }

    // Create update payload
    const updateData = {
      balance: parseFloat(data.amount).toFixed(2)
    };
    
    // Update the bank account's balance field
    const response = await axiosInstance.patch(`/bank-accounts/${encodeURIComponent(bankAccountId)}/`, updateData);

    // Validate response
    if (!response.data || typeof response.data.id !== 'number') {
      throw new Error('Invalid response data structure');
    }

    const updatedBankAccount = response.data;
    
    // Return balance data in format compatible with old BalanceService
    return {
      id: updatedBankAccount.id,
      amount: updatedBankAccount.balance ? updatedBankAccount.balance.toString() : "0.00",
      bank_account_id: updatedBankAccount.id,
      bank_account_nickname: updatedBankAccount.nickname,
      update_date: new Date().toISOString().split('T')[0]
    };
  } catch (error) {
    throw error;
  }
}

/**
 * Get balance for a bank account using the account's use_as_balance flag
 * This is a transitional function for the migration period
 * @returns {Object} - The tracking bank account's balance
 */
export async function getTrackingAccountBalance() {
  try {
    const response = await axiosInstance.get('/bank-accounts/');
    
    // Find the account marked for balance tracking
    const trackingAccount = response.data.find(account => account.include_in_tracking === true);
    
    if (!trackingAccount) {
      // Fallback to first account if none marked for tracking
      const firstAccount = response.data[0];
      if (firstAccount) {
        return await getBalance(firstAccount.id);
      }
      throw new Error('No bank accounts found');
    }
    
    return await getBalance(trackingAccount.id);
  } catch (error) {
    throw error;
  }
}

/**
 * Update balance for the tracking account
 * This is a transitional function for the migration period
 * @param {Object} data - Balance update data
 * @returns {Object} - Updated balance data
 */
export async function updateTrackingAccountBalance(data) {
  try {
    const response = await axiosInstance.get('/bank-accounts/');
    
    // Find the account marked for balance tracking
    const trackingAccount = response.data.find(account => account.include_in_tracking === true);
    
    if (!trackingAccount) {
      throw new Error('No tracking account found');
    }
    
    return await updateBalance(trackingAccount.id, data);
  } catch (error) {
    throw error;
  }
}

export async function getConsolidatedBankAccountData(bankAccountId, relativeFortnight = 0) {
  // Input validation
  if (!bankAccountId || typeof bankAccountId !== 'number') {
    throw new Error('Invalid bank account ID');
  }
  if (typeof relativeFortnight !== 'number') {
    throw new Error('Invalid relative fortnight parameter');
  }

  try {
    const response = await axiosInstance.get(
      `/bank-accounts/${encodeURIComponent(bankAccountId)}/consolidated/`,
      {
        params: {
          relative_fortnight_index: relativeFortnight
        }
      }
    );

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid consolidated bank account data response');
    }

    const data = response.data;

    // Validate response structure
    if (!data.bank_account || !data.requirements || !data.fortnight_context) {
      throw new Error('Invalid consolidated bank account data structure');
    }

    // Additional validation for required fields
    if (typeof data.bank_account.id !== 'number' ||
        typeof data.bank_account.nickname !== 'string' ||
        typeof data.bank_account.balance !== 'string') {
      throw new Error('Invalid bank account data in response');
    }

    if (typeof data.requirements.total_fortnightly !== 'number' ||
        !Array.isArray(data.requirements.breakdown)) {
      throw new Error('Invalid requirements data in response');
    }

    return data;
  } catch (error) {
    throw error;
  }
}

